import React from "react";
import type { Project } from "../../../../../api/useProjects";
import { useImpactChartType } from "./useImpactChartType";
import { comparatorBy } from "diagnostics-lib/comparator";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import t from "../../../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { useTranslations } from "../../../../../api/useTranslations";
import type { Quantity } from "../../../../../api/useQuantities";

export const GLOBAL_DIAGNOSIS_CHART = "GLOBAL_DIAGNOSIS_CHART";

export const useDiagnosisChartType = ({
  project,
  quantities,
}: {
  project: Project;
  quantities: Quantity[];
}) => {
  const translations = useTranslations();
  const sortByQoiOrder = React.useCallback(
    (qs: Quantity[]) => qs.filter(withKeyNotNull("qoiOrder")).sort(comparatorBy("qoiOrder")),
    [],
  );
  const [chartType, setChartType] = useImpactChartType({
    scope: project.id,
    defaultValue: sortByQoiOrder(quantities)[0]?.id || GLOBAL_DIAGNOSIS_CHART,
  });
  const chartTypeOptions = React.useMemo(() => {
    const options = sortByQoiOrder(quantities).map(q => ({
      label: t("diagnosis.overview.header.option", q),
      value: String(q.id),
    }));

    options.push({
      label: t("diagnosis.overview.header.option.global"),
      value: GLOBAL_DIAGNOSIS_CHART,
    });
    return options as NonEmptyArray<{ label: string; value: string }>;
  }, [quantities, sortByQoiOrder]);
  const chartTypeDescriptions = React.useMemo(
    () => [
      {
        label: t("diagnosis.overview.header.option.qoi"),
        value: "QOI",
        description: t("diagnosis.overview.header.option.qoi.description", { translations }),
      },
      {
        label: t("diagnosis.overview.header.option.global"),
        value: GLOBAL_DIAGNOSIS_CHART,
        description: t("diagnosis.overview.header.option.global.description"),
      },
    ],
    [translations],
  );

  const chartTypeOption = React.useMemo(
    () => chartTypeOptions.find(c => c.value === chartType) || chartTypeOptions[0],
    [chartTypeOptions, chartType],
  );
  const setChartTypeOption = React.useCallback(
    (value: string) => {
      setChartType(value);
    },
    [setChartType],
  );

  return {
    chartType,
    chartTypeDescriptions,
    chartTypeOptions,
    chartTypeOption,
    setChartTypeOption,
  };
};
